/**
 * Die Oberflaeche der laufenden Partie.
 *
 * Sie liest den Zustand und baut daraus die Seitenleiste: Spieler, Wuerfel, Aktionen,
 * Besitzkarte, Protokoll. Entscheidungen trifft sie keine - sie meldet gewaehlte
 * Aktionen an den Aufrufer zurueck, der sie durch den Regelkern schickt.
 */
import { HOTELS, type HotelId, stufen } from '../core/data/hotels'
import { FELDER, angrenzendeGelaende } from '../core/data/spielplan'
import type { Aktion, Zustand } from '../core/typen'
import {
  besitzVon,
  buchwert,
  moeglicheAktionen,
  spieler,
  sterne,
  vermoegen,
} from '../core/spiel'
import { beschrifte, geld, phasenText } from './beschriftung'
import { baueRegelhilfe } from './regelhilfe'
import { tonAn, tonUmschalten } from './klang'

export interface Oberflaeche {
  readonly buehne: HTMLElement
  /** Blendet die Regelhilfe ein oder aus. */
  regelnUmschalten(): void
  /** Sind die Regeln gerade offen? */
  regelnOffen(): boolean
  /** Zeichnet die Seitenleiste neu. */
  zeichne(z: Zustand): void
  /** Setzt einen Hinweistext ueber dem Brett, etwa "Feld waehlen". */
  hinweis(text: string | null): void
}

export interface Rueckmeldungen {
  waehle(a: Aktion): void
  /** Ein Feld fuer einen Eingang soll auf dem Brett gewaehlt werden. */
  feldWahl(kandidaten: readonly Aktion[]): void
  neuStarten(): void
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  klasse?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag)
  if (klasse) n.className = klasse
  if (text !== undefined) n.textContent = text
  return n
}

export function baueOberflaeche(
  wurzel: HTMLElement,
  rueck: Rueckmeldungen,
): Oberflaeche {
  wurzel.replaceChildren()
  const partie = el('div', 'partie')
  const buehne = el('div', 'buehne')
  const seite = el('aside', 'seite')
  partie.append(buehne, seite)
  wurzel.append(partie)

  const kopf = el('header', 'kopf')
  const titel = el('h1')
  titel.innerHTML = 'Fünf <b>Sterne</b>'
  const rechts = el('div', 'kopf-rechts')
  const runde = el('span', 'runde')
  const tonknopf = el('button', 'kopf-knopf ton')
  const tonpunkt = el('span', 'ton-punkt')
  tonknopf.append(tonpunkt, el('span', undefined, 'Ton'))
  const zeigeTon = (): void => {
    tonknopf.classList.toggle('aus', !tonAn())
    tonknopf.setAttribute('aria-pressed', String(tonAn()))
    tonknopf.setAttribute('aria-label', tonAn() ? 'Ton ausschalten' : 'Ton einschalten')
  }
  zeigeTon()
  tonknopf.title = 'Ton ein- oder ausschalten'
  tonknopf.addEventListener('click', () => {
    tonUmschalten()
    zeigeTon()
  })
  const regelknopf = el('button', 'kopf-knopf', 'Regeln')
  regelknopf.title = 'Spielregeln anzeigen (Taste R)'
  rechts.append(runde, tonknopf, regelknopf)
  kopf.append(titel, rechts)

  const spielerliste = el('div', 'spielerliste')
  const mitte = el('div', 'mitte')
  const protokoll = el('div', 'protokoll')
  seite.append(kopf, spielerliste, mitte, protokoll)

  const hinweisFeld = el('div', 'hinweis')
  hinweisFeld.style.display = 'none'
  buehne.append(hinweisFeld)

  const hilfe = baueRegelhilfe()
  partie.append(hilfe.wurzel)
  regelknopf.addEventListener('click', hilfe.umschalten)

  function hinweis(text: string | null): void {
    hinweisFeld.textContent = text ?? ''
    hinweisFeld.style.display = text ? '' : 'none'
  }

  function zeichne(z: Zustand): void {
    runde.textContent = `Runde ${z.runde}`
    zeichneSpieler(z, spielerliste)
    zeichneMitte(z, mitte, rueck)
    zeichneProtokoll(z, protokoll)
    zeichneEnde(z, buehne, rueck)
  }

  return {
    buehne,
    zeichne,
    hinweis,
    regelnUmschalten: hilfe.umschalten,
    regelnOffen: () => !hilfe.wurzel.hidden,
  }
}

function zeichneSpieler(z: Zustand, ziel: HTMLElement): void {
  ziel.replaceChildren()
  for (const s of z.spieler) {
    const zeile = el('div', 'spieler')
    if (s.id === z.amZug && z.phase !== 'ende') zeile.classList.add('dran')
    if (s.ausgeschieden) zeile.classList.add('raus')

    const balken = el('div', 'balken')
    balken.style.background = s.farbe

    const mitte = el('div')
    const name = el('div', 'name')
    name.append(el('span', undefined, s.name))
    name.append(el('span', 'art', s.bot ? 'Bot' : 'Mensch'))
    const besitz = besitzVon(z, s.id)
    const hotels = el(
      'div',
      'hotels',
      s.ausgeschieden
        ? 'ausgeschieden'
        : besitz.length === 0
          ? 'kein Grundstück'
          : besitz
              .map((b) => {
                const st = sterne(z, b.hotel)
                return `${HOTELS[b.hotel].name}${st > 0 ? ' ' + '★'.repeat(st) : ''}`
              })
              .join(' · '),
    )
    mitte.append(name, hotels)

    const konto = el('div', 'konto', geld(s.konto))
    konto.append(el('span', 'vermoegen', `Vermögen ${geld(vermoegen(z, s.id))}`))

    zeile.append(balken, mitte, konto)
    ziel.append(zeile)
  }
}

function zeichneProtokoll(z: Zustand, ziel: HTMLElement): void {
  ziel.replaceChildren()
  for (const e of z.protokoll.slice(-40)) {
    const zeile = el('div', 'zeile')
    if (e.spieler !== null) {
      const b = el('b', undefined, `${z.spieler[e.spieler]?.name ?? '?'} `)
      zeile.append(b)
    }
    zeile.append(document.createTextNode(e.text))
    ziel.append(zeile)
  }
  ziel.scrollTop = ziel.scrollHeight
}

function tafel(titel: string): HTMLElement {
  const t = el('section', 'tafel')
  t.append(el('h2', undefined, titel))
  return t
}

function zeichneMitte(z: Zustand, ziel: HTMLElement, rueck: Rueckmeldungen): void {
  ziel.replaceChildren()
  const dran = spieler(z, z.phase === 'auktion' && z.auktion ? z.auktion.amZug : z.amZug)

  const lage = tafel(z.phase === 'auktion' ? 'Versteigerung' : 'Am Zug')
  const kopfzeile = el('div', 'zugkopf')
  const marke = el('div', 'zugmarke')
  marke.textContent = dran.name.slice(0, 1).toUpperCase()
  marke.style.background = dran.farbe
  marke.style.color = '#fff'
  const text = el('div')
  text.append(el('div', undefined, dran.name))
  const unter = el('div')
  unter.style.color = 'var(--text-leise)'
  unter.style.fontSize = '13px'
  unter.textContent =
    z.phase === 'auktion' && z.auktion
      ? `${HOTELS[z.auktion.hotel].name} · Höchstgebot ${geld(z.auktion.hoechstgebot)}`
      : phasenText(z)
  text.append(unter)
  kopfzeile.append(marke, text)
  lage.append(kopfzeile)

  if (z.schuld) {
    const not = el('div')
    not.style.color = 'var(--ziegel)'
    not.style.marginBottom = '10px'
    not.textContent = `Offen: ${geld(z.schuld.betrag)} (${z.schuld.grund})`
    lage.append(not)
  }

  const wuerfel = wuerfelZeile(z)
  if (wuerfel) lage.append(wuerfel)

  const rechnung = z.anzeige.rechnung
  if (rechnung) {
    const blatt = el('div', 'rechnung')
    blatt.append(
      el('div', 'rechnung-kopf', `Übernachtung im ${HOTELS[rechnung.hotel].name}`),
    )
    const formel = el('div', 'rechnung-formel')
    formel.append(
      el('span', undefined, `${rechnung.naechte} × ${geld(rechnung.satz)}`),
      el('b', undefined, geld(rechnung.betrag)),
    )
    blatt.append(formel)
    blatt.append(
      el(
        'div',
        'rechnung-fuss',
        `an ${z.spieler[rechnung.an]?.name ?? '?'} · ${'★'.repeat(sterne(z, rechnung.hotel))}`,
      ),
    )
    lage.append(blatt)
  }

  if (dran.bot && z.phase !== 'ende') {
    // Kein Mensch am Zug: keine anklickbaren Aktionen anbieten. Sonst kann man
    // fuer einen Bot mitspielen - der Probelauf hat genau das getan.
    const warten = el('div', 'wartet')
    warten.append(el('span', 'punkte'), el('span', undefined, `${dran.name} überlegt`))
    lage.append(warten)
    ziel.append(lage)
    for (const hotel of interessanteHotels(z)) ziel.append(besitzkarte(z, hotel))
    return
  }

  const aktionen = el('div', 'aktionen')
  for (const eintrag of aktionsGruppen(z)) {
    const knopf = el('button', `aktion ${eintrag.art}`)
    knopf.append(el('span', undefined, eintrag.text))
    if (eintrag.preis) knopf.append(el('span', 'preis', eintrag.preis))
    knopf.addEventListener('click', () => {
      if (eintrag.kandidaten) rueck.feldWahl(eintrag.kandidaten)
      else if (eintrag.aktion) rueck.waehle(eintrag.aktion)
    })
    aktionen.append(knopf)
  }
  lage.append(aktionen)
  ziel.append(lage)

  for (const hotel of interessanteHotels(z)) {
    ziel.append(besitzkarte(z, hotel))
  }
}

const BAUWUERFEL_ANZEIGE: Record<string, { text: string; farbe: string; titel: string }> = {
  gruen: { text: '', farbe: '#3f9a5c', titel: 'Grün: genehmigt, Baupreis zahlen' },
  rot: { text: '', farbe: '#c2503a', titel: 'Rot: Genehmigung abgelehnt' },
  h: { text: 'H', farbe: '#e9e4d7', titel: 'H: kostenlos bauen' },
  doppelt: { text: '2', farbe: '#e9e4d7', titel: '2: doppelter Baupreis' },
}

/** Die zuletzt geworfenen Würfel, so wie sie auf dem Tisch lägen. */
function wuerfelZeile(z: Zustand): HTMLElement | null {
  const a = z.anzeige
  if (a.wurf === null && a.naechte === null && a.bauwurf === null) return null
  const reihe = el('div', 'wuerfelzeile')

  if (a.wurf !== null) reihe.append(augenWuerfel(a.wurf, 'Gezogen'))
  if (a.naechte !== null) reihe.append(augenWuerfel(a.naechte, 'Nächte'))
  if (a.bauwurf !== null) {
    const w = BAUWUERFEL_ANZEIGE[a.bauwurf]!
    const kasten = el('div', 'wuerfel bau')
    kasten.title = w.titel
    kasten.style.background = w.farbe
    kasten.style.color = w.farbe === '#e9e4d7' ? '#22262e' : '#ffffff'
    kasten.textContent = w.text
    reihe.append(beschriftet(kasten, 'Bauwürfel'))
  }
  return reihe
}

/** Ein Standardwürfel mit echten Augen statt einer Ziffer. */
function augenWuerfel(augen: number, unterschrift: string): HTMLElement {
  const muster: Record<number, readonly (readonly [number, number])[]> = {
    1: [[1, 1]],
    2: [
      [0, 0],
      [2, 2],
    ],
    3: [
      [0, 0],
      [1, 1],
      [2, 2],
    ],
    4: [
      [0, 0],
      [2, 0],
      [0, 2],
      [2, 2],
    ],
    5: [
      [0, 0],
      [2, 0],
      [1, 1],
      [0, 2],
      [2, 2],
    ],
    6: [
      [0, 0],
      [2, 0],
      [0, 1],
      [2, 1],
      [0, 2],
      [2, 2],
    ],
  }
  const kasten = el('div', 'wuerfel')
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 3 3')
  svg.setAttribute('width', '30')
  svg.setAttribute('height', '30')
  for (const [x, y] of muster[augen] ?? []) {
    const punkt = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    punkt.setAttribute('cx', String(x + 0.5))
    punkt.setAttribute('cy', String(y + 0.5))
    punkt.setAttribute('r', '0.38')
    punkt.setAttribute('fill', '#22262e')
    svg.append(punkt)
  }
  kasten.append(svg)
  return beschriftet(kasten, unterschrift)
}

function beschriftet(kasten: HTMLElement, text: string): HTMLElement {
  const huelle = el('div', 'wuerfel-huelle')
  huelle.append(kasten, el('span', 'wuerfel-text', text))
  return huelle
}

interface Knopf {
  readonly text: string
  readonly preis: string
  readonly art: string
  readonly aktion?: Aktion
  /** Mehrere Aktionen, die sich nur im Feld unterscheiden: erst Feld waehlen. */
  readonly kandidaten?: readonly Aktion[]
}

/**
 * Aktionen zu Knoepfen buendeln. Eingangsaktionen unterscheiden sich nur durch das
 * Feld - daraus wird ein Knopf, der auf dem Brett ein Feld waehlen laesst, statt
 * dreissig fast gleicher Zeilen.
 */
function aktionsGruppen(z: Zustand): Knopf[] {
  const alle = moeglicheAktionen(z)
  const out: Knopf[] = []
  const eingaenge = new Map<string, Aktion[]>()

  for (const a of alle) {
    if (a.art === 'eingang-kaufen' || a.art === 'gratis-eingang') {
      const schluessel = `${a.art}|${a.hotel}`
      const liste = eingaenge.get(schluessel) ?? []
      liste.push(a)
      eingaenge.set(schluessel, liste)
      continue
    }
    const b = beschrifte(z, a)
    out.push({ text: b.text, preis: b.preis, art: b.art, aktion: a })
  }

  for (const [schluessel, liste] of eingaenge) {
    const [art, hotel] = schluessel.split('|') as ['eingang-kaufen' | 'gratis-eingang', HotelId]
    out.push({
      text: `Eingang ${HOTELS[hotel].name} aufstellen`,
      preis:
        art === 'gratis-eingang'
          ? 'geschenkt · Feld wählen'
          : `${geld(HOTELS[hotel].eingang)} · Feld wählen`,
      art: art === 'gratis-eingang' ? 'wichtig' : 'normal',
      kandidaten: liste,
    })
  }

  // Zug beenden gehoert ans Ende.
  out.sort((a, b) => Number(a.aktion?.art === 'zug-beenden') - Number(b.aktion?.art === 'zug-beenden'))
  return out
}

/** Welche Besitzkarten gerade weiterhelfen. */
function interessanteHotels(z: Zustand): HotelId[] {
  if (z.phase === 'ende') return []
  if (z.phase === 'auktion' && z.auktion) return [z.auktion.hotel]
  const s = spieler(z, z.amZug)
  const feld = FELDER[s.feld]!
  const eingang = z.eingaenge.find((e) => e.feld === s.feld)
  if (z.phase === 'uebernachten' && eingang) return [eingang.hotel]
  if (feld.art === 'kauf') return [...angrenzendeGelaende(feld)]
  if (z.rechte.darfGenehmigungBeantragen) {
    return besitzVon(z, z.amZug)
      .filter((b) => b.gebaeude < HOTELS[b.hotel].gebaeude.length)
      .map((b) => b.hotel)
      .slice(0, 2)
  }
  // Sonst der eigene Besitz: dann steht immer etwas Nuetzliches in der Leiste.
  return besitzVon(z, z.amZug)
    .map((b) => b.hotel)
    .slice(0, 3)
}

/** Eine Besitzkarte als Tabelle, mit hervorgehobener aktueller Stufe. */
function besitzkarte(z: Zustand, id: HotelId): HTMLElement {
  const hotel = HOTELS[id]
  const besitz = z.besitz[id]
  const karte = el('div', 'karte')

  const kopf = el('div', 'karte-kopf', hotel.name.toUpperCase())
  kopf.style.background = hotel.farbe
  karte.append(kopf)

  const tabelle = el('table')
  const zeile = (links: string, rechts: string, aktiv = false): void => {
    const tr = el('tr')
    if (aktiv) tr.className = 'aktiv'
    tr.append(el('td', undefined, links), el('td', undefined, rechts))
    tabelle.append(tr)
  }

  zeile(
    'Besitzer',
    besitz ? (z.spieler[besitz.besitzer]?.name ?? '-') : 'frei',
  )
  zeile('Grundstück', geld(hotel.grundstueck))
  zeile('Zwangsverkauf', geld(hotel.zwangsverkauf))
  zeile('Jeder Eingang', geld(hotel.eingang))
  if (besitz) zeile('Buchwert', geld(buchwert(besitz)))

  const alle = stufen(hotel)
  const gebaut = besitz ? (besitz.freizeitpark ? alle.length : besitz.gebaeude) : 0
  alle.forEach((stufe, i) => {
    const ist = i + 1 === gebaut
    zeile(
      `${stufe.bezeichnung} · ${'★'.repeat(stufe.sterne)}`,
      `${geld(stufe.kosten)} → ${geld(stufe.nachtsatz)}/Nacht`,
      ist,
    )
  })

  karte.append(tabelle)
  return karte
}

function zeichneEnde(z: Zustand, buehne: HTMLElement, rueck: Rueckmeldungen): void {
  const alt = buehne.querySelector('.ende')
  if (alt) alt.remove()
  if (z.phase !== 'ende') return
  const huelle = el('div', 'ende')
  const karte = el('div', 'ende-karte')
  karte.append(el('h2', undefined, 'Partie beendet'))
  const sieger = z.sieger === null ? null : spieler(z, z.sieger)
  karte.append(
    el('p', undefined, sieger ? `${sieger.name} gewinnt` : 'Niemand bleibt übrig'),
  )
  const knopf = el('button', 'knopf-gross', 'Neue Partie')
  knopf.addEventListener('click', () => rueck.neuStarten())
  karte.append(knopf)
  huelle.append(karte)
  buehne.append(huelle)
}
